// Limits concurrent tile fetches and loads center/nearby tiles first so
// zoomed-out views fill progressively instead of stalling on hundreds of
// simultaneous requests.

import {tileBBoxForKey} from './quadtree.js';
import {tileDiag} from './tile-load-diagnostics.js';

const TILE_LOAD_CONCURRENCY = 14;

function tileKey(z, x, y) {
  return `${z},${x},${y}`;
}

function bboxCenterDistSq(bbox, lon, lat) {
  if (!bbox) return Infinity;
  const cx = (bbox[0] + bbox[2]) / 2;
  const cy = (bbox[1] + bbox[3]) / 2;
  let dx = cx - lon;
  if (dx > 180) dx -= 360;
  if (dx < -180) dx += 360;
  const dy = cy - lat;
  return dx * dx + dy * dy;
}

export class TileLoadScheduler {
  constructor({loadOne, needsLoad, getIndex}) {
    this.loadOne = loadOne;
    this.needsLoad = needsLoad;
    this.getIndex = getIndex;
    this.generation = 0;
    this.queue = [];
    this.inFlight = 0;
    this.visibleSet = new Set();
  }

  syncVisible(visible, cameraLon, cameraLat, meta = {}) {
    this.generation++;
    const gen = this.generation;
    this.visibleSet = new Set(visible.map(t => tileKey(...t)));

    const index = this.getIndex();
    const pending = visible.filter(t => this.needsLoad(...t));
    pending.sort((a, b) => {
      const ba = tileBBoxForKey(index, tileKey(...a));
      const bb = tileBBoxForKey(index, tileKey(...b));
      return bboxCenterDistSq(ba, cameraLon, cameraLat) - bboxCenterDistSq(bb, cameraLon, cameraLat);
    });

    this.queue = pending;
    tileDiag.beginWave({
      visible: visible.length,
      pending: pending.length,
      zoom: meta.zoom,
      lon: cameraLon,
      lat: cameraLat
    });
    if (pending.length === 0) {
      tileDiag.waveComplete({queueLeft: 0, inFlight: 0});
    }
    this.pump(gen);
  }

  isVisible(z, x, y) {
    return this.visibleSet.has(tileKey(z, x, y));
  }

  pump(gen) {
    while (this.inFlight < TILE_LOAD_CONCURRENCY && this.queue.length > 0) {
      if (gen !== this.generation) return;
      const next = this.queue.shift();
      if (!this.isVisible(...next)) continue;
      if (!this.needsLoad(...next)) continue;

      this.inFlight++;
      this.loadOne(...next, gen)
        .catch(() => {})
        .finally(() => {
          this.inFlight--;
          if (gen === this.generation) {
            if (this.queue.length === 0 && this.inFlight === 0) {
              tileDiag.waveComplete({queueLeft: 0, inFlight: 0});
            }
            this.pump(gen);
          }
        });
    }
  }

  snapshot() {
    return {queueLeft: this.queue.length, inFlight: this.inFlight};
  }
}
